<template>
  <div class="mb-4 flex justify-end py-2">
    <LinkToCreate :to="{ name: 'userCreate' }">
      Add User
    </LinkToCreate>
  </div>

  <BaseCard class="relative">
    <QueryLoading v-if="isLoading" />
    <QueryError
      v-else-if="isError"
      :show-retry="true"
      class="m-auto"
      @retry="refetch"
    >
      Failed to load users
    </QueryError>
    <template v-else>
      <div class="mb-2 flex w-full">
        <SearchInput v-model="rawSearch" />
      </div>
      <BaseTable
        v-model:ordering="ordering"
        :rows="data?.items ?? []"
        :columns="columns"
        :is-loading="isFetching"
        :get-row-class="getRowClass"
      >
        <template #cell-email="{ row, value }">
          <span
            v-if="!row.isActive"
            class="mr-2 text-error"
          >
            INACTIVE
          </span>

          <span
            v-if="row.id === currentUser?.id"
            class="mr-2 text-info"
          >
            YOU
          </span>

          {{ value }}

          <!-- This div just acts as a spacer to increase height of table row -->
          <div
            v-if="showCurrentUserWarning(row.email)"
            class="mt-5 hidden h-12 md:block"
          >
            <!-- Absolutley positioned relative to tbody -->
            <div
              class="absolute inset-x-3 flex items-center bg-warning/5 text-sm italic text-warning"
            >
              <IconExclamationTriangle
                class="mr-1 size-6"
              />
              <p
                class="flex-1 whitespace-normal break-words text-sm"
              >
                {{
                  currentUser?.isMerchantGroupAdmin
                    ? `This ${merchantName} user has the same email
                address as the currently logged in Merchant
                Group Admin. Changing your Group Admin's
                password will not affect this user's password
                and vice versa.`
                    : `This ${merchantName} user has the same email address as the currently logged in staff user. Changing your staff user's password will not affect this user's password and vice versa`
                }}
              </p>
            </div>
          </div>
        </template>

        <template #cell-actions="{ row }">
          <div class="flex w-full justify-end">
            <BaseMenu>
              <template #default>
                <BaseMenuItem
                  :to="{
                    name: 'userEdit',
                    params: {
                      userId: row.id,
                    },
                  }"
                >
                  Edit User
                </BaseMenuItem>

                <BaseMenuItem
                  v-if="row.isActive"
                  @click="onSendUserLoginLink(row.id)"
                >
                  Send Portal Login Link
                </BaseMenuItem>

                <template v-if="row.id !== currentUser?.id">
                  <BaseMenuItem
                    v-if="row.isActive"
                    @click="onDeactivateUser(row.id)"
                  >
                    <span class="text-error">
                      Deactivate User
                    </span>
                  </BaseMenuItem>

                  <BaseMenuItem
                    v-else
                    @click="onActivateUser(row.id)"
                  >
                    <span class="text-primary">
                      Activate User
                    </span>
                  </BaseMenuItem>
                </template>
              </template>
            </BaseMenu>
          </div>

          <!-- Mobile current user warning banner -->
          <div
            v-if="showCurrentUserWarning(row.email)"
            class="mt-2 flex w-full items-center bg-warning/5 px-3 py-2 italic text-warning md:hidden"
          >
            <IconExclamationTriangle
              class="mr-2 size-6 flex-none"
            />
            <p class="text-sm">
              {{
                currentUser?.isMerchantGroupAdmin
                  ? `This ${merchantName} user has the same email
                address as the currently logged in Merchant
                Group Admin. Changing your Group Admin's
                password will not affect this user's password
                and vice versa.`
                  : `This ${merchantName} user has the same email address as the currently logged in staff user. Changing your staff user's password will not affect this user's password and vice versa`
              }}
            </p>
          </div>
        </template>
      </BaseTable>

      <div class="flex justify-end py-2">
        <BasePagination
          v-model:page="page"
          :page-count="pageCount"
        />
      </div>
    </template>
  </BaseCard>

  <BaseConfirmation
    ref="activateUserConfirmationRef"
    title="Activate User"
    description="This will allow the user to log in to the Web Portal either via requesting a log in link or by using their password if it is set"
    confirm-label="Activate"
    is-positive
  />
  <BaseConfirmation
    ref="deactivateUserConfirmationRef"
    title="Deactivate User"
    confirm-label="Deactivate"
  >
    Deactivating a user will prevent them from being able to
    log into the web portal
    <br />
    <br />
    <div class="flex items-center text-sm">
      <IconExclamationCircle
        class="mr-2 size-8 text-info"
      />
      A user can be reactivated either on this page, or by
      requesting a log in link through the
      {{ organizationConfig?.appName }}
      application
    </div>
  </BaseConfirmation>
</template>

<script setup lang="ts">
import type {
  TableColumn,
  TableOrdering,
} from "@/components/BaseTable.vue"
import type {
  ListUsersFilter,
  ListUsersOrdering,
  ListUsersResponseItem,
} from "@/services/users/listUsers"

const { organizationConfig } = useOrganizationConfig()
const { currentUser } = useCurrentUser()
const { name: merchantName } = useTenant()
const rawSearch = ref("")

const debouncedSearch = debouncedRef(rawSearch, 300)

const filters = computed<ListUsersFilter>(() => ({
  search: debouncedSearch.value.trim(),
}))

const {
  page,
  pageCount,
  paginationQuery,
  resetPagination,
} = usePagination({
  count: computed(() => data.value?.count || 0),
  limit: 10,
})

const ordering = ref<
  TableOrdering<ListUsersOrdering> | undefined
>()

watch(
  () => filters,
  () => resetPagination(),
  { deep: true }
)

const query = reactiveComputed(() => ({
  ...filters.value,
  ordering: ordering.value,
  ...paginationQuery.value,
}))

const { data, isLoading, isFetching, isError, refetch } =
  useUsers(query)

const now = useNow()

type ColumnId =
  | "email"
  | "name"
  | "lastLogin"
  | "createdAt"
  | "actions"

const columns: Array<
  TableColumn<
    ListUsersResponseItem,
    ColumnId,
    ListUsersOrdering
  >
> = [
  {
    id: "email",
    header: "Email",
    getCellValue: (row) => row.email,
    isSortable: true,
    autoWidth: true,
  },
  {
    id: "name",
    header: "Name",
    getCellValue: (row) =>
      `${row.firstName} ${row.lastName}`,
    isSortable: true,
  },
  {
    id: "lastLogin",
    header: "Last Login",
    getCellValue: (row) =>
      row.lastLogin
        ? DateFns.formatDistance(row.lastLogin, now.value, {
            addSuffix: true,
          })
        : "",
    align: "right",
    autoWidth: true,
    isSortable: true,
  },
  {
    id: "createdAt",
    header: "Account Created",
    getCellValue: (row) =>
      row.createdAt
        ? DateFns.formatDistance(row.createdAt, now.value, {
            addSuffix: true,
          })
        : "",
    align: "right",
    autoWidth: true,
    isSortable: true,
  },

  {
    id: "actions",
    header: "",
    align: "right",
    autoWidth: true,
  },
]

const activateUserConfirmationRef = useTemplateRef(
  "activateUserConfirmationRef"
)
const onActivateUser = async (
  userId: ListUsersResponseItem["id"]
) => {
  if (activateUserConfirmationRef.value === null) return

  const { isCanceled } =
    await activateUserConfirmationRef.value.reveal()
  if (isCanceled) return
  activateUser(userId)
}

const deactivateUserConfirmationRef = useTemplateRef(
  "deactivateUserConfirmationRef"
)
const onDeactivateUser = async (
  userId: ListUsersResponseItem["id"]
) => {
  if (deactivateUserConfirmationRef.value === null) return

  const { isCanceled } =
    await deactivateUserConfirmationRef.value.reveal()
  if (isCanceled) return
  deactivateUser(userId)
}

const onSendUserLoginLink = (
  userId: ListUsersResponseItem["id"]
) => sendUserLoginLink(userId)

const getRowClass = (row: ListUsersResponseItem) => {
  const classes = []

  if (!row.isActive) {
    classes.push("text-background-fg/30")
  }

  if (showCurrentUserWarning(row.email)) {
    classes.push("align-top")
  }

  return classes.join(" ")
}

const showCurrentUserWarning = (rowEmail: string) => {
  return (
    currentUser.value?.isShadowUser &&
    rowEmail === currentUser.value.displayEmail
  )
}
</script>
